import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CustomerHeader from '../components/CustomerHeader';
import './OrderConfirmation.css';
import { supabase } from '../config/supabase';

const OrderConfirmation = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (id) {
      fetchOrderDetails();
    } else {
      setLoading(false);
    }
  }, [id]);

  const fetchOrderDetails = async () => {
    try {
      const { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;

      if (data) {
        setOrder(data);
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCartItemCount = () => {
    return 0; // Cart is cleared after order
  };

  if (loading) {
    return (
      <div className="order-confirmation-page">
        <CustomerHeader
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          cartItemCount={getCartItemCount()}
          onCartClick={() => navigate('/cart')}
        />
        <div className="order-confirmation-loading">
          <div className="order-confirmation-loading-spinner">⏳</div>
          <p>Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-confirmation-page">
        <CustomerHeader
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          cartItemCount={getCartItemCount()}
          onCartClick={() => navigate('/cart')}
        />
        <div className="order-confirmation-error">
          <div className="order-confirmation-error-icon">❌</div>
          <h2>Order Not Found</h2>
          <p>We couldn't find your order. Please contact support if you need assistance.</p>
          <button
            className="order-confirmation-home-btn"
            onClick={() => navigate('/')}
          >
            Go to Home
          </button>
        </div>
      </div>
    );
  }

  // Parse items if it's a string
  const orderItems = typeof order.items === 'string' 
    ? JSON.parse(order.items) 
    : order.items;

  return (
    <div className="order-confirmation-page">
      <CustomerHeader
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        cartItemCount={getCartItemCount()}
        onCartClick={() => navigate('/cart')}
      />

      {/* Success Header */}
      <div className="order-confirmation-header">
        <div className="order-confirmation-header-container">
          <div className="order-confirmation-success-icon">✓</div>
          <h1 className="order-confirmation-title">Thank You for Your Order!</h1>
          <p className="order-confirmation-subtitle">
            Your order has been placed successfully and we'll start preparing it right away.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="order-confirmation-main">
        <div className="order-confirmation-container">
          <div className="order-confirmation-content">
            {/* Left Column - Order Details */}
            <div className="order-confirmation-details-section">
              {/* Order Info Card */}
              <div className="order-confirmation-card">
                <h2 className="order-confirmation-card-title">Order Information</h2>
                
                <div className="order-confirmation-info-row">
                  <span className="order-confirmation-info-label">Order ID:</span>
                  <span className="order-confirmation-info-value">#{order.id.slice(0, 8).toUpperCase()}</span>
                </div>

                <div className="order-confirmation-info-row">
                  <span className="order-confirmation-info-label">Order Date:</span>
                  <span className="order-confirmation-info-value">
                    {new Date(order.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>

                <div className="order-confirmation-info-row">
                  <span className="order-confirmation-info-label">Status:</span>
                  <span className={`order-confirmation-status order-confirmation-status-${order.status}`}>
                    {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                  </span>
                </div>

                <div className="order-confirmation-info-row">
                  <span className="order-confirmation-info-label">Payment Method:</span>
                  <span className="order-confirmation-info-value">
                    {order.payment_method === 'cash' ? 'Cash on Delivery' : 'Card Payment'}
                  </span>
                </div>
              </div>

              {/* Delivery Address Card */}
              <div className="order-confirmation-card">
                <h2 className="order-confirmation-card-title">Delivery Address</h2>
                <p className="order-confirmation-address">{order.delivery_address}</p>
                {order.delivery_instructions && (
                  <div className="order-confirmation-instructions">
                    <span className="order-confirmation-instructions-label">Special Instructions:</span>
                    <span className="order-confirmation-instructions-text">{order.delivery_instructions}</span>
                  </div>
                )}
              </div>

              {/* Order Items Card */}
              <div className="order-confirmation-card">
                <h2 className="order-confirmation-card-title">Order Items</h2>
                <div className="order-confirmation-items-list">
                  {orderItems.map((item, index) => (
                    <div key={index} className="order-confirmation-item">
                      <div className="order-confirmation-item-info">
                        <span className="order-confirmation-item-name">{item.name}</span>
                        <span className="order-confirmation-item-quantity">Qty: {item.quantity}</span>
                      </div>
                      <span className="order-confirmation-item-price">
                        Rs. {(item.price * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Order Summary */}
            <div className="order-confirmation-summary-section">
              <div className="order-confirmation-summary-card">
                <h2 className="order-confirmation-summary-title">Order Summary</h2>

                <div className="order-confirmation-summary-row">
                  <span className="order-confirmation-summary-label">Subtotal</span>
                  <span className="order-confirmation-summary-value">
                    Rs. {parseFloat(order.subtotal).toFixed(2)}
                  </span>
                </div>

                <div className="order-confirmation-summary-row">
                  <span className="order-confirmation-summary-label">Delivery Fee</span>
                  <span className="order-confirmation-summary-value">
                    {parseFloat(order.delivery_fee) === 0 ? (
                      <span className="order-confirmation-free">Free</span>
                    ) : (
                      `Rs. ${parseFloat(order.delivery_fee).toFixed(2)}`
                    )}
                  </span>
                </div>

                <div className="order-confirmation-total-row">
                  <span className="order-confirmation-total-label">Total</span>
                  <span className="order-confirmation-total-amount">
                    Rs. {parseFloat(order.total).toFixed(2)}
                  </span>
                </div>

                {/* Next Steps */}
                <div className="order-confirmation-next-steps">
                  <h3 className="order-confirmation-next-steps-title">What's Next?</h3>
                  <ul className="order-confirmation-steps-list">
                    <li>We'll send you an order confirmation email shortly</li>
                    <li>Your order will be prepared and delivered to your address</li>
                    <li>You'll receive updates about your order status</li>
                    {order.payment_method === 'cash' && (
                      <li>Please keep cash ready for delivery</li>
                    )}
                  </ul>
                </div>

                {/* Action Buttons */}
                <div className="order-confirmation-actions">
                  <button
                    className="order-confirmation-primary-btn"
                    onClick={() => navigate('/menu')}
                  >
                    Continue Shopping
                  </button>
                  <button
                    className="order-confirmation-secondary-btn"
                    onClick={() => navigate('/')}
                  >
                    Back to Home
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderConfirmation;

